/**
 * AtomicBoxPlugin
 *
 * An editor plugin that renders AI-generated atomic elements.
 * Each box stores a ComposedElement JSON (from the scene-architect) together
 * with pre-rendered HTML and CSS variables fetched from the middleware.
 *
 * State format (stored URI-encoded in box.state.__text):
 * {
 *   elementData:  { type, ...elementFields }   ← content without layout
 *   themeId:      string | null
 *   paletteId:    string | null
 *   renderedHtml: string | null                 ← cached HTML fragment
 *   cssVariables: Record<string, string>        ← CSS custom properties map
 * }
 *
 * Supported element types (20):
 *   Text    : eyebrow, heading, footer
 *   Lists   : bullet-list, bullet, card-grid
 *   Narrative: timeline, quote, two-column, comparison, hero, section
 *   Data    : stat-group, stats, chart
 *   Media   : image
 *   UI atoms: divider, badge, cta-button, icon-block
 */

import React from "react";
import AtomicElementRenderer from "./components/AtomicElementRenderer";

/* eslint-disable react/prop-types */

export function AtomicBoxPlugin(base) {
  return {
    // ─── Plugin metadata ────────────────────────────────────────────────────
    getConfig() {
      return {
        name: "AtomicBoxPlugin",
        displayName: IdealStudio.i18n.t("AtomicBoxPlugin.PluginName"),
        category: "ai-content",
        needsConfigModal: false,
        needsTextEdition: false,
        needsXMLEdition: false,
        flavor: "react",
        initialWidth: "40%",
        initialWidthSlide: "40%",
        initialHeight: "auto",
        initialHeightSlide: "auto",
        icon: "auto_awesome",
        allowFloatingBox: true,
        // Custom flags recognised by streaming/mapper layer
        isAtomicElement: true,
      };
    },

    // ─── Toolbar ────────────────────────────────────────────────────────────
    // Minimal toolbar: shows the element type (read-only) + opacity control.
    // Full edit fields per type are intentionally deferred — the element data
    // can be re-rendered by updating the stored elementState and re-fetching.
    getToolbar(state) {
      return {
        main: {
          __name: "Main",
          accordions: {
            style: {
              __name: IdealStudio.i18n.t("AtomicBoxPlugin.accordion_style"),
              icon: "palette",
              buttons: {
                padding: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.padding"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 100,
                },
                backgroundColor: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.background_color"),
                  type: "color",
                  value: "transparent",
                },
                borderWidth: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.border_width"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 10,
                },
                borderStyle: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.border_style"),
                  type: "select",
                  value: "solid",
                  options: [
                    "none",
                    "hidden",
                    "dotted",
                    "dashed",
                    "solid",
                    "double",
                    "groove",
                    "ridge",
                    "inset",
                    "outset",
                    "initial",
                    "inherit",
                  ],
                },
                borderColor: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.border_color"),
                  type: "color",
                  value: "#000000",
                },
                borderRadius: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.border_radius"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 50,
                },
                opacity: {
                  __name: IdealStudio.i18n.t("AtomicBoxPlugin.opacity"),
                  type: "range",
                  value: 1,
                  min: 0,
                  max: 1,
                  step: 0.01,
                },
              },
            },
          },
        },
      };
    },

    // ─── Render ─────────────────────────────────────────────────────────────
    // getRenderTemplate is called by base_plugin for both editor and visor mode.
    // props may include marks, exercises, themeId/paletteId passed by the canvas.
    //
    // boxId is sourced from props.id (set by EditorBox/VisorBox via ...this.props)
    // or props.boxId (set explicitly by core/visor/base_plugin.es6:167).
    //
    // visorCurrentTime is box-relative scene time. The live Visor also passes
    // the scene origin and id so field animations can follow sceneTimeBus.
    // The editor passes its time through editorLayerTimes.
    getRenderTemplate(state, props) {
      return (
        <AtomicElementRenderer
          state={state}
          boxId={props?.id || props?.boxId || null}
          themeId={props?.themeId || null}
          paletteId={props?.paletteId || null}
          themeCssVariables={props?.themeCssVariables || null}
          onUpdate={props?.update || null}
          visorPlaying={props?.sceneAdvancing ?? props?.visorPlaying}
          visorCurrentTime={
            typeof props?.visorCurrentTime === "number" ? props.visorCurrentTime : null
          }
          animationTimeOriginSec={props?.animationTimeOriginSec ?? null}
          animationSceneId={props?.animationSceneId ?? null}
          fontScale={state.fontScale || 1}
        />
      );
    },
  };
}
